/**
 * Thin REST client for the boost-core /campaigns API.
 *
 * Uses the existing `apiFetch` infra (cookie auth, CSRF on mutations, 401 recovery).
 * Endpoints, payloads and naming mirror apps/boostcore/src/campaign/.
 *
 * Coexists with the legacy V1 CampaignApi (under api-swagger/) which proxies to
 * the BS2 backend → Odoo. To be deprecated once /campaign/* is removed.
 */
import { apiFetch } from '@/auth/apiFetch'
import type {
  AddCompaniesCsvResult,
  AddCompaniesGeoPayload,
  AddCompaniesManualPayload,
  AddCompaniesResult,
  EventBatch,
  CallSession,
  CallSessionStatus,
  CampaignDetail,
  CampaignParticipant,
  CampaignWithCounters,
  CreateCampaignPayload,
  PaginatedCampaigns,
  SearchCampaignsParams,
  UpdateCampaignPayload,
} from '@/types/campaigns'

function qs(params: Record<string, unknown> | object): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  )
  if (entries.length === 0) return ''
  const usp = new URLSearchParams()
  for (const [k, v] of entries) usp.set(k, String(v))
  return `?${usp.toString()}`
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`${res.status} ${res.statusText}${body ? ` — ${body}` : ''}`)
  }
  if (res.status === 204) return undefined as unknown as T
  return (await res.json()) as T
}

const jsonHeaders = { 'Content-Type': 'application/json' }

// -- Campaigns ---------------------------------------------------------------

export function createCampaign(payload: CreateCampaignPayload): Promise<CampaignWithCounters> {
  return apiFetch('/campaigns', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  }).then(json<CampaignWithCounters>)
}

export function listPrivate(params: SearchCampaignsParams = {}): Promise<PaginatedCampaigns> {
  return apiFetch(`/campaigns/private${qs(params)}`).then(json<PaginatedCampaigns>)
}

export function listManagerOwned(params: SearchCampaignsParams = {}): Promise<PaginatedCampaigns> {
  return apiFetch(`/campaigns/manager${qs(params)}`).then(json<PaginatedCampaigns>)
}

export function listManagerAsParticipant(
  params: SearchCampaignsParams = {},
): Promise<PaginatedCampaigns> {
  return apiFetch(`/campaigns/manager/salesforce${qs(params)}`).then(json<PaginatedCampaigns>)
}

export function getDetails(uuid: string): Promise<CampaignDetail> {
  return apiFetch(`/campaigns/${uuid}`).then(json<CampaignDetail>)
}

export function update(uuid: string, payload: UpdateCampaignPayload): Promise<CampaignWithCounters> {
  return apiFetch(`/campaigns/${uuid}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  }).then(json<CampaignWithCounters>)
}

export async function remove(uuid: string): Promise<void> {
  await apiFetch(`/campaigns/${uuid}`, { method: 'DELETE' })
}

export function archive(uuid: string): Promise<CampaignWithCounters> {
  return apiFetch(`/campaigns/${uuid}/archive`, { method: 'PUT' }).then(json<CampaignWithCounters>)
}

export function unarchive(uuid: string): Promise<CampaignWithCounters> {
  return apiFetch(`/campaigns/${uuid}/unarchive`, { method: 'PUT' }).then(json<CampaignWithCounters>)
}

// -- Participants ------------------------------------------------------------

export function addParticipants(uuid: string, userUuids: string[]): Promise<CampaignParticipant[]> {
  return apiFetch(`/campaigns/${uuid}/participants/add`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify({ userUuids }),
  }).then(json<CampaignParticipant[]>)
}

/**
 * Remove a single participant. PENDING sessions assigned to this participant
 * MUST be redistributed to other active participants (round-robin).
 * `redistributeTo` is mandatory if the participant has at least one PENDING.
 * The server blocks removal of the last active participant.
 */
export function removeParticipant(
  campaignUuid: string,
  userUuid: string,
  redistributeTo: string[],
): Promise<CampaignParticipant[]> {
  return apiFetch(`/campaigns/${campaignUuid}/participants/${userUuid}/remove`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify({ redistributeTo }),
  }).then(json<CampaignParticipant[]>)
}

// -- Add companies -----------------------------------------------------------

export function addCompaniesManual(
  uuid: string,
  payload: AddCompaniesManualPayload,
): Promise<AddCompaniesResult> {
  return apiFetch(`/campaigns/${uuid}/companies/manual`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  }).then(json<AddCompaniesResult>)
}

export function addCompaniesCsv(
  uuid: string,
  file: File,
  assigneeUuids?: string[],
): Promise<AddCompaniesCsvResult> {
  const form = new FormData()
  form.append('file', file)
  if (assigneeUuids?.length) form.append('assigneeUuids', JSON.stringify(assigneeUuids))
  return apiFetch(`/campaigns/${uuid}/companies/csv`, {
    method: 'POST',
    body: form,
  }).then(json<AddCompaniesCsvResult>)
}

export function addCompaniesGeo(
  uuid: string,
  payload: AddCompaniesGeoPayload,
): Promise<AddCompaniesResult> {
  return apiFetch(`/campaigns/${uuid}/companies/geo`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  }).then(json<AddCompaniesResult>)
}

export interface CompanyGeoPreview {
  uuid: string
  name: string
  siret: string | null
  zip: string | null
  latitude: number | null
  longitude: number | null
  currentStatus: string | null
  referenceExterne: string | null
  nafCode: { code: string | null; label: string | null } | null
}

export interface PreviewByGeoResult {
  count: number
  companies: CompanyGeoPreview[]
}

export function previewCompaniesGeo(
  payload: AddCompaniesGeoPayload,
): Promise<PreviewByGeoResult> {
  return apiFetch(`/campaigns/companies/geo/preview`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  }).then(json<PreviewByGeoResult>)
}

// -- Timeline ----------------------------------------------------------------

export function getTimeline(uuid: string): Promise<EventBatch[]> {
  return apiFetch(`/campaigns/${uuid}/timeline`).then(json<EventBatch[]>)
}

export interface BatchCallDetailRow {
  uuid: string
  occurredAt: string
  company: {
    uuid: string
    name: string
    siret: string | null
    referenceExterne: string | null
  }
}

/** Détail des call sessions associées à un batch CALL_COMPLETED ou
 *  CALL_REOPENED de la timeline (streak agrégée par le back). Le back lit la
 *  fenêtre [startedAt, endedAt] depuis le contexte du batch. */
export function getBatchCallDetail(
  campaignUuid: string,
  batchUuid: string,
): Promise<BatchCallDetailRow[]> {
  return apiFetch(
    `/campaigns/${campaignUuid}/event-batches/${batchUuid}/calls`,
  ).then(json<BatchCallDetailRow[]>)
}

// -- Call sessions -----------------------------------------------------------

export function listSessions(campaignUuid: string): Promise<CallSession[]> {
  return apiFetch(`/campaigns/${campaignUuid}/call-sessions`).then(json<CallSession[]>)
}

export function updateSession(
  uuid: string,
  payload: { status?: CallSessionStatus; assignedUserUuid?: string },
): Promise<CallSession> {
  return apiFetch(`/call-sessions/${uuid}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  }).then(json<CallSession>)
}

export function dispatchSessions(
  campaignUuid: string,
  callSessionUuids: string[],
  assigneeUuids: string[],
): Promise<{ dispatched: number; assignees: number }> {
  return apiFetch(`/campaigns/${campaignUuid}/call-sessions/dispatch`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ callSessionUuids, assigneeUuids }),
  }).then(json<{ dispatched: number; assignees: number }>)
}

/** Redistribute selected sessions evenly over the campaign's active participants
 *  (round-robin). The server picks the participant pool — caller doesn't pass it.
 *  Only PENDING sessions are redistributed; DONE / CANCELLED are reported under `skipped`. */
export function reassignEqual(
  campaignUuid: string,
  callSessionUuids: string[],
): Promise<{ dispatched: number; assignees: number; skipped: number }> {
  return apiFetch(`/campaigns/${campaignUuid}/call-sessions/reassign-equal`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ callSessionUuids }),
  }).then(json<{ dispatched: number; assignees: number; skipped: number }>)
}

export async function deleteSession(uuid: string): Promise<void> {
  await apiFetch(`/call-sessions/${uuid}`, { method: 'DELETE' })
}

export function bulkDeleteSessions(
  campaignUuid: string,
  callSessionUuids: string[],
): Promise<{ deleted: number }> {
  return apiFetch(`/campaigns/${campaignUuid}/call-sessions`, {
    method: 'DELETE',
    headers: jsonHeaders,
    body: JSON.stringify({ callSessionUuids }),
  }).then(json<{ deleted: number }>)
}
